import type { ClientToServer, ServerToClient, JoinMsg } from "@riffbox/protocol";
import { reconnectDelay, DEFAULT_BACKOFF, type BackoffConfig } from "./reconnect.js";

export type ConnState = "connecting" | "open" | "reconnecting" | "closed";

export interface WsClientOptions {
  url: string;
  /** Resent automatically on every (re)connect so the server rebuilds room state. */
  join: JoinMsg;
  onMessage: (msg: ServerToClient) => void;
  onState?: (state: ConnState) => void;
  /** Fired after a *re*connect (not the first connect) so callers can re-sync. */
  onReconnect?: () => void;
  backoff?: BackoffConfig;
}

/**
 * Typed WebSocket client with silent auto-reconnect (PRD §6). A wifi blip must
 * not surface a scary modal — it reconnects quietly with capped jittered backoff
 * and re-sends `join`. Callers re-derive everything else from the fresh
 * `room_state` the server replies with.
 */
export class WsClient {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private closedByUser = false;
  private everConnected = false;
  private state: ConnState = "closed";

  constructor(private readonly opts: WsClientOptions) {}

  get connectionState(): ConnState {
    return this.state;
  }

  connect(): void {
    this.closedByUser = false;
    this.open();
  }

  send(msg: ClientToServer): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }

  /** Test seam: simulate a dropped connection (a wifi blip) → silent reconnect. */
  dropForTest(): void {
    this.ws?.close();
  }

  close(): void {
    this.closedByUser = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.ws?.close();
    this.ws = null;
    this.setState("closed");
  }

  private setState(s: ConnState): void {
    this.state = s;
    this.opts.onState?.(s);
  }

  private open(): void {
    this.setState(this.everConnected ? "reconnecting" : "connecting");
    const ws = new WebSocket(this.opts.url);
    this.ws = ws;

    ws.addEventListener("open", () => {
      const wasReconnect = this.everConnected;
      this.everConnected = true;
      this.attempt = 0;
      this.setState("open");
      ws.send(JSON.stringify(this.opts.join));
      if (wasReconnect) this.opts.onReconnect?.();
    });

    ws.addEventListener("message", (ev) => {
      let msg: ServerToClient;
      try {
        msg = JSON.parse(typeof ev.data === "string" ? ev.data : "") as ServerToClient;
      } catch {
        return; // ignore malformed frames
      }
      this.opts.onMessage(msg);
    });

    const scheduleReconnect = () => {
      if (this.closedByUser) {
        this.setState("closed");
        return;
      }
      const delay = reconnectDelay(this.attempt++, this.opts.backoff ?? DEFAULT_BACKOFF);
      this.setState("reconnecting");
      this.timer = setTimeout(() => this.open(), delay);
    };

    ws.addEventListener("close", scheduleReconnect);
    ws.addEventListener("error", () => {
      // `error` is followed by `close`; let close drive the reconnect.
      try {
        ws.close();
      } catch {
        /* already closing */
      }
    });
  }
}
